import { AuthenticationResult, PopupRequest, SilentRequest } from '@azure/msal-browser';
import { msalInstance, loginRequest } from './msalConfig';
import { authService } from '../services/authService';

export class MsalAuthService {
  // Login with popup
  static async loginWithPopup(request: PopupRequest = loginRequest): Promise<AuthenticationResult | null> {
    try {
      const response = await msalInstance.loginPopup(request);
      
      if (response) {
        console.log('MSAL login successful, account:', response.account?.name);
        console.log('Token claims:', response.idTokenClaims);
        
        // Call backend to exchange the token for a session
        // Pass both accessToken and idToken for more flexibility
        const idToken = response.idToken || undefined;
        await this.authenticateWithBackend(response.accessToken, idToken);
        return response;
      }
      return null;
    } catch (error) {
      console.error('Error during MSAL login:', error);
      throw error;
    }
  }
  
  // Login with redirect
  static loginWithRedirect(request = loginRequest): void {
    msalInstance.loginRedirect(request);
  }
  
  // Logout
  static logout(): void {
    msalInstance.logout();
  }
  
  // Acquire token silently
  static async acquireTokenSilent(request: SilentRequest): Promise<string | null> {
    try {
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed', error);
      return null;
    }
  }
  
  // Get current account
  static getAccount() {
    const currentAccounts = msalInstance.getAllAccounts();
    if (currentAccounts.length === 0) {
      return null;
    } else if (currentAccounts.length > 1) {
      console.warn('Multiple accounts detected, using first account');
    }
    return currentAccounts[0];
  }
  
  // Authenticate with backend API
  static async authenticateWithBackend(accessToken: string, idToken?: string): Promise<boolean> {
    try {
      console.log('Authenticating with backend using tokens:', {
        accessTokenAvailable: !!accessToken,
        idTokenAvailable: !!idToken
      });
      
      // Call your backend API to validate the token and create a session
      console.log('Calling authService.loginWithEntra...');
      const response = await authService.loginWithEntra(accessToken, idToken);
      console.log('Backend authentication response:', response);
      
      // The response is the direct data object, not wrapped in success/data properties
      // Based on console logs, it contains accessToken and user directly
      if (response && response.accessToken && response.user) {
        console.log('Authentication successful, response data:', response);
        
        // Store the token and user information in localStorage
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        console.log('Successfully stored user data in localStorage');
        console.log('User data:', JSON.parse(localStorage.getItem('user') || '{}'));
        console.log('Token stored:', !!localStorage.getItem('token'));
        
        // Update AuthContext state directly instead of reloading
        // This will be done by dispatching an event that AuthContext can listen to
        const authStateEvent = new CustomEvent('authStateChanged', {
          detail: { user: response.user, token: response.accessToken }
        });
        window.dispatchEvent(authStateEvent);
        
        return true;
      }
      console.log('Authentication failed: Invalid or missing response data');
      return false;
    } catch (error) {
      console.error('Backend authentication failed:', error);
      return false;
    }
  }
}
